/**
 * OrderReviewStars Component
 *
 * Compact review indicator that shows average rating as stars
 * Clicking opens a modal with full review details
 */

'use client';

import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stars } from '@/src/shared/ui/rating-stars/Stars';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { useModal } from '@/src/shared/hooks/useModal';
import { reviewsApi } from '@/src/shared/api/reviews.api';
import { cn } from '@/src/shared/lib/utils';

interface OrderReviewStarsProps {
  orderId: string;
  userRole: 'client' | 'worker';
  orderTitle?: string;
  workerName?: string;
  workerAvatarUrl?: string;
  clientName?: string;
  clientAvatarUrl?: string;
  className?: string;
}

/**
 * OrderReviewStars - compact review indicator
 */
export const OrderReviewStars: FC<OrderReviewStarsProps> = ({
  orderId,
  userRole,
  orderTitle,
  workerName,
  workerAvatarUrl,
  clientName,
  clientAvatarUrl,
  className,
}) => {
  const { open: openReviewDetailsModal } = useModal('review-details');

  // Fetch order reviews
  const { data, isLoading } = useQuery({
    queryKey: ['order-reviews', orderId],
    queryFn: () => reviewsApi.getOrderReviews(orderId),
    staleTime: 30000, // 30 seconds
  });

  const { reviews, canReview } = data || { reviews: [], canReview: false };

  // If no reviews and can't review, don't show anything
  if (!isLoading && reviews.length === 0 && !canReview) {
    return null;
  }

  // Calculate average rating from all reviews
  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + (r.averageRating || 0), 0) / reviews.length
      : 0;

  // Show CTA to leave review if no reviews yet but can review
  if (reviews.length === 0 && canReview) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openReviewDetailsModal({
            orderId,
            userRole,
            orderTitle,
            workerName,
            workerAvatarUrl,
            clientName,
            clientAvatarUrl,
          });
        }}
        className={cn(
          'flex items-center gap-1.5 text-left hover:opacity-70 transition-opacity',
          className
        )}
      >
        <Typography variant="bodySm" color="tertiary" className="underline">
          Leave a review
        </Typography>
      </button>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        <div className="w-16 h-4 bg-gray-200 animate-pulse rounded" />
      </div>
    );
  }

  // Show stars with rating
  if (reviews.length > 0) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openReviewDetailsModal({
            orderId,
            userRole,
            orderTitle,
            workerName,
            workerAvatarUrl,
            clientName,
            clientAvatarUrl,
          });
        }}
        className={cn(
          'flex items-center gap-1.5 hover:opacity-70 transition-opacity',
          className
        )}
      >
        <Stars count={5} rating={averageRating} size="xs" />
        <Typography variant="bodySm" weight="medium" color="primary">
          {averageRating.toFixed(1)}
        </Typography>
        <Typography variant="bodySm" color="tertiary">
          ({reviews.length})
        </Typography>
      </button>
    );
  }

  return null;
};
